// src/pages/SuperAdmin/SubscriptionsManager.jsx
import React, { useState } from 'react';
import { Row, Col, Table, Button, Input, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label } from 'reactstrap';
import { post } from 'helpers/api_helper';

const SubscriptionsManager = ({ subscriptions, plans, loading, onRefresh }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');

  // Extend Modal
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [extendDays, setExtendDays] = useState(14);
  const [submitting, setSubmitting] = useState(false);

  const filteredSubs = subscriptions.filter((s) => {
    const matchSearch =
      (s.clinic?.clinic_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.client?.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.plan?.name || '').toLowerCase().includes(search.toLowerCase());

    if (filterStatus === 'active') return matchSearch && s.status === 'active';
    if (filterStatus === 'trial') return matchSearch && s.status === 'trial';
    if (filterStatus === 'expired') return matchSearch && s.status === 'expired';

    return matchSearch;
  });

  const handleExtend = async (e) => {
    e.preventDefault();
    if (!selectedSub) return;
    setSubmitting(true);
    try {
      await post(`/superadmin/subscriptions/${selectedSub.id}/extend`, {
        days: Number(extendDays),
      });
      setIsExtendModalOpen(false);
      onRefresh();
    } catch (err) {
      alert(err?.message || 'Failed to extend subscription');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="sa-card">
        <div className="sa-card-header">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <h5 className="sa-card-title">
              <i className="bx bx-receipt text-warning" /> Tenant Subscriptions Ledger ({filteredSubs.length})
            </h5>
            <div className="d-flex align-items-center gap-2">
              <Input
                type="text"
                className="form-control-sm"
                placeholder="Search clinics or plans..."
                style={{ width: 240 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Input
                type="select"
                className="form-select-sm"
                style={{ width: 140 }}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Subscriptions</option>
                <option value="active">Active Paid</option>
                <option value="trial">Free Trial</option>
                <option value="expired">Expired</option>
              </Input>
            </div>
          </div>
        </div>

        <div className="sa-card-body p-0">
          <div className="table-responsive">
            <Table className="table align-middle table-hover table-nowrap mb-0 font-size-12">
              <thead className="table-light">
                <tr>
                  <th>Clinic / Tenant</th>
                  <th>Subscription Tier</th>
                  <th>Billing Cycle</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Start Date</th>
                  <th>Expiry / Renewal</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      {loading ? 'Loading subscriptions...' : 'No subscriptions recorded.'}
                    </td>
                  </tr>
                ) : (
                  filteredSubs.map((sub) => {
                    const isActive = sub.status === 'active';
                    const isTrial = sub.status === 'trial';
                    const isExpired = sub.status === 'expired';

                    return (
                      <tr key={sub.id}>
                        <td>
                          <div className="font-weight-semibold text-dark font-size-13">
                            {sub.clinic?.clinic_name || 'Clinic'}
                          </div>
                          <small className="text-muted">{sub.client?.email || '-'}</small>
                        </td>

                        <td>
                          <div className="font-weight-semibold">{sub.plan?.name || 'Custom Plan'}</div>
                        </td>

                        <td>
                          <span className="badge bg-light text-dark text-capitalize border">
                            {sub.billing_cycle || 'monthly'}
                          </span>
                        </td>

                        <td>
                          <div className="font-weight-bold text-dark">
                            ₹{Number(sub.amount || 0).toLocaleString()}
                          </div>
                        </td>

                        <td>
                          <span className={`status-badge ${isActive ? 'active' : isTrial ? 'trial' : 'expired'}`}>
                            <i className={`bx ${isActive ? 'bx-check-circle' : isTrial ? 'bx-time-five' : 'bx-error-circle'}`} />
                            {isActive ? 'Active Paid' : isTrial ? 'Free Trial' : 'Expired'}
                          </span>
                        </td>

                        <td>{new Date(sub.start_date).toLocaleDateString()}</td>

                        <td>
                          {sub.expiry_date ? (
                            <div>{new Date(sub.expiry_date).toLocaleDateString()}</div>
                          ) : (
                            <span className="text-muted">Lifetime</span>
                          )}
                        </td>

                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-warning text-dark font-weight-semibold"
                            onClick={() => {
                              setSelectedSub(sub);
                              setIsExtendModalOpen(true);
                            }}
                          >
                            <i className="bx bx-plus me-1" /> Extend Validity
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {/* Extend Validity Modal */}
      <Modal isOpen={isExtendModalOpen} toggle={() => setIsExtendModalOpen(!isExtendModalOpen)} centered>
        <ModalHeader toggle={() => setIsExtendModalOpen(!isExtendModalOpen)}>
          <i className="bx bx-time text-warning me-2" /> Extend Subscription / Trial Validity
        </ModalHeader>
        <Form onSubmit={handleExtend}>
          <ModalBody>
            <div className="p-3 bg-light rounded mb-3 font-size-12">
              <div><strong>Clinic:</strong> {selectedSub?.clinic?.clinic_name}</div>
              <div><strong>Plan:</strong> {selectedSub?.plan?.name}</div>
              <div>
                <strong>Current Expiry:</strong>{' '}
                {selectedSub?.expiry_date ? new Date(selectedSub.expiry_date).toLocaleDateString() : 'N/A'}
              </div>
            </div>

            <FormGroup className="mb-3">
              <Label className="font-weight-semibold font-size-12">Extend By (Days) *</Label>
              <Input
                type="number"
                required
                min="1"
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
              />
              <small className="text-muted">
                Adds additional days to the subscription or trial expiration date.
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setIsExtendModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? 'Extending...' : 'Confirm Extension'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionsManager;
